import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatDistanceToNow } from 'date-fns';

const QualityMetrics = ({ metrics, compact = false, detailed = false }) => {
  if (!metrics) return null;

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 85) return 'bg-green-50 border-green-200';
    if (score >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getImprovementIcon = (value) => {
    if (value > 0) return { icon: 'TrendingUp', color: 'text-green-600' };
    if (value < 0) return { icon: 'TrendingDown', color: 'text-red-600' };
    return { icon: 'Minus', color: 'text-gray-600' };
  };

  const metricsData = [
    {
      label: 'Total Leads',
      value: metrics?.totalLeads?.toLocaleString(),
      icon: 'Users',
      color: 'text-blue-600'
    },
    {
      label: 'Validated',
      value: metrics?.validatedCount?.toLocaleString(),
      icon: 'CheckCircle',
      color: 'text-green-600'
    },
    {
      label: 'Duplicates Found',
      value: metrics?.duplicateCount?.toLocaleString(),
      icon: 'Copy',
      color: 'text-yellow-600'
    },
    {
      label: 'Data Quality Score',
      value: `${metrics?.dataQualityScore}%`,
      icon: 'Award',
      color: getScoreColor(metrics?.dataQualityScore)
    }
  ];

  const verificationMetrics = [
    {
      label: 'Email Verification Rate',
      value: `${metrics?.emailVerificationRate}%`,
      target: 90,
      icon: 'Mail'
    },
    {
      label: 'Phone Verification Rate',
      value: `${metrics?.phoneVerificationRate}%`,
      target: 85,
      icon: 'Phone'
    },
    {
      label: 'Profile Completeness',
      value: `${metrics?.profileCompletenessAvg?.toFixed(1)}%`,
      target: 80,
      icon: 'User'
    },
    {
      label: 'AI Confidence Score',
      value: `${metrics?.confidenceScoreAvg?.toFixed(1)}%`,
      target: 85,
      icon: 'Brain'
    }
  ];

  if (compact) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quality Metrics</h3>
        
        <div className="space-y-4">
          {metricsData?.slice(0, 2)?.map((metric, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-muted ${metric?.color}`}>
                <Icon name={metric?.icon} size={16} />
              </div>
              <div>
                <div className="font-semibold text-foreground">{metric?.value}</div>
                <div className="text-sm text-muted-foreground">{metric?.label}</div>
              </div>
            </div>
          ))}
          
          <div className={`p-3 border rounded-lg ${getScoreBackground(metrics?.dataQualityScore)}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Quality</span>
              <span className={`text-lg font-bold ${getScoreColor(metrics?.dataQualityScore)}`}>
                {metrics?.dataQualityScore}%
              </span>
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground">
            Last updated {formatDistanceToNow(metrics?.lastUpdated, { addSuffix: true })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metricsData?.map((metric, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 text-center">
            <div className={`inline-flex p-3 rounded-full bg-muted mb-3 ${metric?.color}`}>
              <Icon name={metric?.icon} size={24} />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{metric?.value}</div>
            <div className="text-sm text-muted-foreground">{metric?.label}</div>
          </div>
        ))}
      </div>

      {/* Data Quality Score */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Data Quality Analysis</h3>
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-green-600" />
            <span className="text-sm text-green-600">+{metrics?.improvementPotential}% potential</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className={`text-center p-6 border-2 rounded-lg ${getScoreBackground(metrics?.dataQualityScore)}`}>
              <div className={`text-4xl font-bold mb-2 ${getScoreColor(metrics?.dataQualityScore)}`}>
                {metrics?.dataQualityScore}%
              </div>
              <div className="text-sm text-muted-foreground">Overall Quality Score</div>
            </div>
          </div>

          <div className="space-y-3">
            {verificationMetrics?.map((metric, index) => {
              const percentage = parseInt(metric?.value);
              const isAboveTarget = percentage >= metric?.target;
              
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon name={metric?.icon} size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{metric?.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${
                      isAboveTarget ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {metric?.value}
                    </span>
                    <Icon 
                      name={isAboveTarget ? 'CheckCircle' : 'AlertTriangle'} 
                      size={14} 
                      className={isAboveTarget ? 'text-green-600' : 'text-yellow-600'} 
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      {detailed && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Performance Metrics</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-2">{metrics?.processingSpeed}</div>
              <div className="text-sm text-muted-foreground">Processing Speed</div>
              <div className="text-xs text-green-600 mt-1">+12% from last week</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-2">99.2%</div>
              <div className="text-sm text-muted-foreground">Uptime</div>
              <div className="text-xs text-green-600 mt-1">Above SLA target</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 mb-2">2.3s</div>
              <div className="text-sm text-muted-foreground">Avg Response Time</div>
              <div className="text-xs text-green-600 mt-1">-0.4s improvement</div>
            </div>
          </div>
        </div>
      )}

      {/* Quality Trends */}
      {detailed && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quality Improvement Opportunities</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600" />
                <div>
                  <div className="font-medium text-foreground">Email Verification Rate</div>
                  <div className="text-sm text-muted-foreground">
                    Currently at {metrics?.emailVerificationRate}%, target is 90%
                  </div>
                </div>
              </div>
              <div className="text-sm font-medium text-yellow-600">+15% potential</div>
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={20} className="text-blue-600" />
                <div>
                  <div className="font-medium text-foreground">Phone Validation</div>
                  <div className="text-sm text-muted-foreground">
                    {metrics?.phoneVerificationRate}% verified, can improve with AI enhancement
                  </div>
                </div>
              </div>
              <div className="text-sm font-medium text-blue-600">+20% potential</div>
            </div>

            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <div>
                  <div className="font-medium text-foreground">Duplicate Detection</div>
                  <div className="text-sm text-muted-foreground">
                    AI algorithms performing well, {metrics?.duplicateCount} duplicates found
                  </div>
                </div>
              </div>
              <div className="text-sm font-medium text-green-600">On target</div>
            </div>
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="text-center">
        <div className="text-xs text-muted-foreground">
          Last updated {formatDistanceToNow(metrics?.lastUpdated, { addSuffix: true })}
        </div>
      </div>
    </div>
  );
};

export default QualityMetrics;